/**
 * Budget vs Actuals — analytic views over PositionBudget (budgeted_amount vs actual_amount).
 *
 * Overall + monthly trend + variance, filterable across three independent dimension groups:
 *   • F&O Financial Dimensions  — Campus | Department (Financial) | Faculty  (DimensionDisplayValue)
 *   • Role Master taxonomy      — Function | Department (Org) | Team 1-3
 *   • Worker                    — Employment Type (vacant -> "Vacant")
 *
 * All cost figures + breakdowns are server-filtered (GET /budget/summary + /summary/series).
 * Actuals come from Sage; when none are loaded the view flags it rather than showing R0 spend.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import { Loader2, AlertTriangle, X, Filter as FilterIcon, RefreshCw, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  budgetService, BudgetSummary, BudgetSeries, BudgetFilterOptions,
  BudgetAnalyticsFilters, DimensionBreakdownEntry,
} from '@/services/api/organizationService';

const zar = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 });
const fmt = (n?: number) => zar.format(n || 0);
const fmtCompact = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e6) return `R${(n / 1e6).toFixed(1)}m`;
  if (a >= 1e3) return `R${(n / 1e3).toFixed(0)}k`;
  return `R${n.toFixed(0)}`;
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ALL = '__ALL__';

// Three filter groups that never collide (note the two distinct "Department"s).
type FKey = keyof Omit<BudgetAnalyticsFilters, 'year' | 'month'>;
const FILTER_GROUPS: { group: string; items: { key: FKey; optKey: keyof BudgetFilterOptions; label: string }[] }[] = [
  { group: 'F&O Financial Dimensions', items: [
    { key: 'campus', optKey: 'campus', label: 'Campus' },
    { key: 'department', optKey: 'department', label: 'Department (Financial)' },
    { key: 'faculty', optKey: 'faculty', label: 'Faculty' },
  ] },
  { group: 'Role Master', items: [
    { key: 'function', optKey: 'function', label: 'Function' },
    { key: 'rm_department', optKey: 'rm_department', label: 'Department (Org)' },
    { key: 'team_1', optKey: 'team_1', label: 'Team 1' },
    { key: 'team_2', optKey: 'team_2', label: 'Team 2' },
    { key: 'team_3', optKey: 'team_3', label: 'Team 3' },
  ] },
  { group: 'Worker', items: [
    { key: 'employment', optKey: 'employment', label: 'Employment Type' },
  ] },
];
const FILTER_LABEL: Record<string, string> = Object.fromEntries(
  FILTER_GROUPS.flatMap(g => g.items.map(i => [i.key, i.label])),
);

// Breakdown axes the user can group the variance table by (key -> BudgetSummary field).
const GROUP_BYS: { key: keyof BudgetSummary; label: string; filterKey: FKey }[] = [
  { key: 'by_function', label: 'Function', filterKey: 'function' },
  { key: 'by_campus', label: 'Campus', filterKey: 'campus' },
  { key: 'by_department', label: 'Department (Financial)', filterKey: 'department' },
  { key: 'by_rm_department', label: 'Department (Org)', filterKey: 'rm_department' },
  { key: 'by_faculty', label: 'Faculty', filterKey: 'faculty' },
  { key: 'by_team_1', label: 'Team 1', filterKey: 'team_1' },
  { key: 'by_employment', label: 'Employment Type', filterKey: 'employment' },
];

const varianceTone = (variancePct?: number) => {
  const v = variancePct ?? 0;
  if (v < -20) return 'text-red-600';      // >20% over budget
  if (v < 0) return 'text-amber-600';      // over budget
  return 'text-emerald-600';               // at/under budget
};

const BudgetVsActuals: React.FC<{ year?: number; month?: number }> = ({ year, month }) => {
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [series, setSeries] = useState<BudgetSeries | null>(null);
  const [options, setOptions] = useState<BudgetFilterOptions | null>(null);
  const [filters, setFilters] = useState<BudgetAnalyticsFilters>({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [groupBy, setGroupBy] = useState<keyof BudgetSummary>('by_function');

  useEffect(() => { budgetService.getBudgetFilterOptions().then(setOptions).catch(() => {}); }, []);

  const reqFilters = useMemo<BudgetAnalyticsFilters>(() => ({ ...filters, year, month }), [filters, year, month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, ser] = await Promise.all([
        budgetService.getSummary(reqFilters),
        budgetService.getSummarySeries(reqFilters),
      ]);
      setSummary(s);
      setSeries(ser);
    } catch (e) {
      console.error('Budget vs actuals load failed', e);
    } finally {
      setLoading(false);
    }
  }, [reqFilters]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k: FKey, v?: string) => setFilters(f => ({ ...f, [k]: v || undefined }));
  const activeFilters = (Object.entries(filters) as [FKey, string | undefined][]).filter(([, v]) => v);

  // Download the current (filtered) view as a position-level XLSX.
  const doExport = async () => {
    setExporting(true);
    try {
      const blob = await budgetService.exportAnalytics(reqFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const y = reqFilters.year ?? summary?.period_year ?? '';
      const m = String(reqFilters.month ?? summary?.period_month ?? '').padStart(2, '0');
      a.href = url;
      a.download = `budget_vs_actuals_${y}_${m}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Analytics export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const budgeted = summary?.total_budgeted_cost ?? 0;
  const actualGross = summary?.total_actual_cost ?? 0;
  // Headline = CTC basis (actual − bonus/comm + employer contributions) — the
  // RF1-comparable spend. Gross stays visible as a sub-line; falls back to
  // gross while a stale API doesn't return the ctc fields yet.
  const actual = summary?.total_actual_ctc_cost ?? actualGross;
  const variance = summary?.total_variance_ctc ?? summary?.total_variance ?? (budgeted - actual);
  const variancePct = summary?.total_variance_ctc_pct ?? summary?.total_variance_pct ?? 0;
  const actualsAvailable = summary?.actuals_available ?? false;

  const breakdown = (summary?.[groupBy] as DimensionBreakdownEntry[] | undefined) || [];
  const groupByMeta = GROUP_BYS.find(g => g.key === groupBy)!;

  const finalisedThrough = series?.actuals_finalised_through ?? summary?.actuals_finalised_through ?? 0;
  const provisionalMonths = series?.provisional_months ?? summary?.provisional_months ?? [];
  const chartData = (series?.months || []).map(m => {
    const ctc = m.actual_ctc_cost ?? m.actual_cost;
    return {
      name: MONTHS[m.month - 1] + (m.provisional ? '*' : ''),
      'Budgeted': m.budgeted_cost,
      'Actual (CTC)': ctc,
      gross: m.actual_cost,
      bonus: m.bonus_cost ?? 0,
      cc: m.cc_cost ?? 0,
      over: ctc > m.budgeted_cost && !m.provisional,
      provisional: !!m.provisional,
    };
  });
  const hcData = (series?.months || []).map(m => ({
    name: MONTHS[m.month - 1],
    'Active (budgeted seat)': m.active_headcount,
    'Active (unbudgeted seat)': m.unbudgeted_filled_headcount ?? 0,
    'Budgeted vacancy': m.budgeted_vacancies,
  }));
  const hcNow = series?.months?.find(m => m.month === (summary?.period_month ?? 0));

  return (
    <div className="space-y-4">
      {/* Actuals availability banner */}
      {!actualsAvailable && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div><strong>Actuals not loaded for this year.</strong> Figures show budget only — actual
            spend is seeded from Sage. This is not genuine zero spend; run the budget seed (or, in
            PROD, register the Sage EMP_SALARY / EMP_EARNINGS / EMP_EARN_MTH gets) to populate actuals.</div>
        </div>
      )}

      {/* Payroll-finalisation boundary: provisional months are visible but excluded from YTD */}
      {provisionalMonths.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Payroll finalised through {finalisedThrough ? MONTHS[finalisedThrough - 1] : '—'}.</strong>{' '}
            {provisionalMonths.map(m => MONTHS[m - 1]).join(', ')} has posted actuals that are NOT yet
            finalised — shown hatched/starred on the trend and excluded from YTD variance. Adjust under
            “Finalised through” once payroll closes the month.
          </div>
        </div>
      )}

      {/* Filter bar — three groups */}
      <Card>
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
            <FilterIcon className="h-3.5 w-3.5" /> Filters
            <div className="ml-auto flex items-center gap-3">
              {/* Payroll-finalisation override: caps YTD/variance at this month.
                  'Auto' = coverage heuristic decides. */}
              <label className="inline-flex items-center gap-1.5 text-gray-500" title="Actuals finalised through — YTD budget-vs-actual stops at this month until payroll finalises later months. 'Auto' lets the coverage heuristic decide.">
                Finalised through
                <select
                  className="rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-700"
                  value={summary?.finalised_source === 'override' ? String(finalisedThrough || '') : ''}
                  onChange={async (e) => {
                    const v = e.target.value;
                    try {
                      await budgetService.setBudgetPeriodConfig(
                        summary?.period_year ?? year ?? new Date().getFullYear(),
                        v === '' ? null : Number(v),
                      );
                      load();
                    } catch (err) {
                      console.error('Failed to set finalised-through month', err);
                    }
                  }}>
                  <option value="">Auto{summary?.finalised_source !== 'override' && finalisedThrough ? ` (${MONTHS[finalisedThrough - 1]})` : ''}</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </label>
              {activeFilters.length > 0 && (
                <button onClick={() => setFilters({})} className="text-heritage-blue hover:underline">Clear all</button>
              )}
              <button onClick={doExport} disabled={exporting} title="Download this filtered view as XLSX (position-level: dims, Role Master, line manager, variance)"
                className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {FILTER_GROUPS.map(grp => (
              <div key={grp.group} className="space-y-1">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{grp.group}</div>
                <div className="flex flex-wrap gap-2">
                  {grp.items.map(item => {
                    const opts = options?.[item.optKey] || [];
                    if (!opts.length) return null;
                    return (
                      <Select key={item.key} value={filters[item.key] || ALL}
                        onValueChange={v => setFilter(item.key, v === ALL ? undefined : v)}>
                        <SelectTrigger className="h-7 text-xs w-auto min-w-[130px]">
                          <SelectValue placeholder={item.label} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>{item.label}: All</SelectItem>
                          {opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {activeFilters.map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1 rounded-full border border-heritage-blue/30 bg-heritage-blue/5 px-2 py-0.5 text-[11px] text-heritage-blue">
                  <span className="text-gray-500">{FILTER_LABEL[k]}:</span> {v}
                  <button onClick={() => setFilter(k, undefined)} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-2xl font-bold text-heritage-blue tabular-nums">{fmt(budgeted)}</div>
          <div className="text-xs text-gray-500">Budgeted</div>
          <div className="text-[10px] text-gray-400">{summary ? `${MONTHS[(summary.period_month || 1) - 1]} ${summary.period_year}` : ''}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className={cn('text-2xl font-bold tabular-nums', actualsAvailable ? 'text-gray-900' : 'text-gray-300')}>{fmt(actual)}</div>
          <div className="text-xs text-gray-500">Actual (CTC)</div>
          <div className="text-[10px] text-gray-400" title="CTC basis = gross Sage earnings − bonus/commission + employer contributions — like-for-like against the RF1 budget (which is CTC and excludes bonuses)">
            {actualsAvailable ? `excl. bonus/comm · gross ${fmt(actualGross)}` : 'not loaded'}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className={cn('text-2xl font-bold tabular-nums', actualsAvailable ? varianceTone(variancePct) : 'text-gray-300')}>{fmt(variance)}</div>
          <div className="text-xs text-gray-500">Variance (CTC)</div>
          <div className="text-[10px] text-gray-400">{variance >= 0 ? 'under budget' : 'over budget'}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className={cn('text-2xl font-bold tabular-nums', actualsAvailable ? varianceTone(variancePct) : 'text-gray-300')}>{(variancePct).toFixed(1)}%</div>
          <div className="text-xs text-gray-500">Variance %</div>
          <div className="text-[10px] text-gray-400">of budget</div>
        </CardContent></Card>
      </div>

      {/* Monthly trend */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-700">Monthly trend {series ? `· ${series.year}` : ''}</div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={cn('h-3.5 w-3.5 mr-1', loading && 'animate-spin')} /> Refresh
            </Button>
          </div>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                <YAxis tickFormatter={(v) => fmtCompact(Number(v))} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48} />
                <Tooltip labelStyle={{ fontWeight: 600 }} contentStyle={{ fontSize: 12 }}
                  formatter={(v: number, key: string, item: { payload?: { gross?: number; bonus?: number; cc?: number } }) => {
                    if (key === 'Actual (CTC)') {
                      const p = item?.payload;
                      return [`${fmt(v)}  (gross ${fmt(p?.gross ?? 0)} − bonus ${fmt(p?.bonus ?? 0)} + empl. contrib. ${fmt(p?.cc ?? 0)})`, key];
                    }
                    return [fmt(v), key];
                  }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Budgeted" fill="#15305D" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Actual (CTC)" radius={[2, 2, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i}
                      fill={d.provisional ? '#fbbf24' : d.over ? '#dc2626' : '#10b981'}
                      fillOpacity={d.provisional ? 0.45 : 1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">Actual (CTC) = gross Sage earnings − bonus/commission + employer contributions — like-for-like against the RF1 budget, which is CTC-based and carries no bonus provision. Hover a bar for the gross/bonus split.</p>
        </CardContent>
      </Card>

      {/* Headcount trend — active (filled) vs budgeted vacancies, per month (stacked = budgeted headcount) */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-700">Headcount {series ? `· ${series.year}` : ''}</div>
            {hcNow && (
              <div className="flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: '#15305D' }} /> Active (budgeted) <strong className="tabular-nums">{hcNow.active_headcount}</strong></span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: '#64748b' }} /> Active (unbudgeted) <strong className="tabular-nums">{hcNow.unbudgeted_filled_headcount ?? 0}</strong></span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: '#f59e0b' }} /> Budgeted vacancy <strong className="tabular-nums">{hcNow.budgeted_vacancies}</strong></span>
                <span className="text-gray-500">· Total staff <strong className="tabular-nums">{hcNow.total_filled_headcount ?? hcNow.active_headcount}</strong></span>
              </div>
            )}
          </div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={hcData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} allowDecimals={false} />
                <Tooltip labelStyle={{ fontWeight: 600 }} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Active (budgeted seat)" stackId="hc" fill="#15305D" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Active (unbudgeted seat)" stackId="hc" fill="#64748b" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Budgeted vacancy" stackId="hc" fill="#f59e0b" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">Navy + slate = ALL staff on seats that month (month-aware: who actually sat there, not who sits there today). Navy = on an RF1-budgeted seat; slate = on an unbudgeted seat (ICA/sessional envelope staff, unfunded admin); amber = budgeted seats standing empty. Navy + amber = the funded establishment.</p>
        </CardContent>
      </Card>

      {/* Breakdown by dimension, with variance */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <span className="text-sm font-medium text-gray-700 mr-1">Group by</span>
            {GROUP_BYS.map(g => (
              <button key={String(g.key)} onClick={() => setGroupBy(g.key)}
                className={cn('rounded-full border px-2.5 py-0.5 text-[11px]',
                  groupBy === g.key ? 'border-heritage-blue bg-heritage-blue text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
                {g.label}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium">{groupByMeta.label}</th>
                  <th className="text-right px-2 py-1.5 font-medium">Positions</th>
                  <th className="text-right px-2 py-1.5 font-medium">Budgeted</th>
                  <th className="text-right px-2 py-1.5 font-medium" title="CTC basis: gross earnings − bonus/comm + employer contributions — like-for-like against RF1">Actual (CTC)</th>
                  <th className="text-right px-2 py-1.5 font-medium">Variance</th>
                  <th className="text-right px-2 py-1.5 font-medium">Var %</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((r, i) => {
                  const rActual = r.actual_ctc_cost ?? r.actual_cost;
                  const rVar = r.variance_ctc ?? r.variance;
                  const rVarPct = r.variance_ctc_pct ?? r.variance_pct;
                  const over = (rVar ?? 0) < 0;
                  const drill = r.code && r.code !== '__VACANT__' && r.code !== '__UNKNOWN__';
                  return (
                    <tr key={i} className={cn('border-b last:border-0 hover:bg-gray-50',
                      (rVarPct ?? 0) < -20 && 'bg-red-50/40')}>
                      <td className="px-2 py-1.5 max-w-[260px] truncate">
                        {drill ? (
                          <button className="text-left hover:text-heritage-blue hover:underline"
                            title={`Filter to ${r.name}`}
                            onClick={() => setFilter(groupByMeta.filterKey, r.code || undefined)}>
                            {r.name}
                          </button>
                        ) : <span className={r.code ? '' : 'text-gray-400'}>{r.name}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">{r.positions}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.budgeted_cost)}</td>
                      <td className={cn('px-2 py-1.5 text-right tabular-nums', !actualsAvailable && 'text-gray-300')}>{fmt(rActual)}</td>
                      <td className={cn('px-2 py-1.5 text-right tabular-nums', actualsAvailable ? (over ? 'text-red-600' : 'text-emerald-600') : 'text-gray-300')}>{fmt(rVar)}</td>
                      <td className={cn('px-2 py-1.5 text-right tabular-nums', actualsAvailable ? varianceTone(rVarPct) : 'text-gray-300')}>{(rVarPct ?? 0).toFixed(1)}%</td>
                    </tr>
                  );
                })}
                {!breakdown.length && (
                  <tr><td colSpan={6} className="px-2 py-8 text-center text-gray-400">{loading ? 'Loading…' : 'No data for this selection.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BudgetVsActuals;
